import { env } from "@/shared/constants/env";
import type { SupportChatIdentity, TawkApi, TawkCallback } from "../types";

const SCRIPT_ID = "alomain-tawk-widget";
const LOAD_TIMEOUT_MS = 30_000;
const API_TIMEOUT_MS = 30_000;

let loadPromise: Promise<TawkApi> | null = null;
let widgetReady = false;
let resolveLoad: ((api: TawkApi) => void) | null = null;
let rejectLoad: ((error: Error) => void) | null = null;
let openingPromise: Promise<"identified" | "anonymous"> | null = null;
let identifiedUserId: string | null = null;
let stopped = false;
let sessionVersion = 0;

export class TawkTimeoutError extends Error {
  constructor() {
    super("Le chat met plus de temps que prévu à répondre. Réessaie dans quelques instants.");
  }
}

export class TawkCancelledError extends Error {}

function assertSession(version: number) {
  if (version !== sessionVersion) throw new TawkCancelledError();
}

export function isTawkConfigured(): boolean {
  return Boolean(env.tawkPropertyId && env.tawkWidgetId);
}

function getApi(): TawkApi {
  window.Tawk_API ??= {};
  return window.Tawk_API;
}

function callWithCallback(
  invoke: (callback: TawkCallback) => void,
  timeoutMs = API_TIMEOUT_MS,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new TawkTimeoutError());
    }, timeoutMs);

    const done: TawkCallback = (error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (error) reject(error);
      else resolve();
    };

    try {
      invoke(done);
    } catch (error) {
      window.clearTimeout(timer);
      reject(error);
    }
  });
}

/** Charge le widget une seule fois et attend son rendu, pas seulement son script. */
export function loadTawkWidget(): Promise<TawkApi> {
  if (!isTawkConfigured()) {
    return Promise.reject(new Error("Le chat support n'est pas encore configuré."));
  }

  const current = window.Tawk_API;
  if ((widgetReady || stopped) && current) return Promise.resolve(current);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<TawkApi>((resolve, reject) => {
    const api = getApi();
    // Le script n’est injecté qu’au clic sur un accès support. Démarrer ici
    // permet à onLoad d’arriver : autoStart=false attendait une connexion que
    // openTawkChat ne lançait qu’APRÈS la résolution de cette promesse.
    api.autoStart = true;
    // Au-dessus de l'app et des dialogues, mais sous les toasts (z-index 10000).
    api.customStyle = { zIndex: 9_000 };
    window.Tawk_LoadStart = new Date();

    const timer = window.setTimeout(() => {
      rejectLoad?.(new TawkTimeoutError());
    }, LOAD_TIMEOUT_MS);

    resolveLoad = (loadedApi) => {
      window.clearTimeout(timer);
      resolveLoad = null;
      rejectLoad = null;
      resolve(loadedApi);
    };
    rejectLoad = (error) => {
      window.clearTimeout(timer);
      loadPromise = null;
      resolveLoad = null;
      rejectLoad = null;
      reject(error);
    };

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) return;

    // Le SDK capture les callbacks à son initialisation. Garder ces fonctions
    // stables permet aussi à une nouvelle tentative de recevoir un onLoad tardif.
    const previousOnLoad = api.onLoad;
    const previousOnBeforeLoad = api.onBeforeLoad;
    api.onBeforeLoad = () => {
      widgetReady = false;
      previousOnBeforeLoad?.();
    };
    api.onLoad = () => {
      widgetReady = true;
      resolveLoad?.(getApi());
      previousOnLoad?.();
    };

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.charset = "UTF-8";
    script.crossOrigin = "anonymous";
    script.src = `https://embed.tawk.to/${encodeURIComponent(env.tawkPropertyId)}/${encodeURIComponent(env.tawkWidgetId)}`;
    script.onerror = () => {
      script.remove();
      rejectLoad?.(new Error("Impossible de charger le chat support."));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

async function showChat(api: TawkApi, version: number): Promise<void> {
  assertSession(version);
  if (!api.showWidget || !api.maximize || !api.isChatMaximized || !api.isChatHidden) {
    throw new Error("Le chat support n’est pas encore prêt.");
  }
  if (stopped) {
    // login reconnecte lui-même une session arrêtée. Seul le mode anonyme
    // nécessite start, qui ne réémet pas forcément onLoad pour un widget rendu.
    if (!api.start) throw new Error("Le chat support n’est pas encore prêt.");
    api.start({ showWidget: true });
    stopped = false;
  }
  // autoStart et login connectent déjà le widget. Un start supplémentaire
  // réinitialise la connexion et son callback n'est pas un signal de rendu.
  // login peut rappeler avant le nouveau onLoad : attendre ce rendu, puis
  // vérifier l'état effectif du widget avant d'annoncer l'ouverture.
  const deadline = Date.now() + LOAD_TIMEOUT_MS;
  while (true) {
    assertSession(version);
    if (widgetReady) {
      api.showWidget();
      api.maximize();
      if (api.isChatMaximized() && !api.isChatHidden()) return;
    }
    if (Date.now() >= deadline) throw new TawkTimeoutError();
    await new Promise((resolve) => window.setTimeout(resolve, 100));
  }
}

/**
 * Ouvre le chat. Sans identité signée, il reste disponible en mode anonyme :
 * une panne du Gateway ne doit pas supprimer le dernier canal de support.
 */
export function openTawkChat(
  identity?: SupportChatIdentity,
): Promise<"identified" | "anonymous"> {
  if (openingPromise) return openingPromise;
  openingPromise = openChat(identity, sessionVersion).finally(() => {
    openingPromise = null;
  });
  return openingPromise;
}

async function openChat(identity: SupportChatIdentity | undefined, version: number): Promise<"identified" | "anonymous"> {
  const api = await loadTawkWidget();
  assertSession(version);

  if (!identity || !api.login) {
    await showChat(api, version);
    api.addTags?.(["identity-unavailable"], () => undefined);
    return "anonymous";
  }

  let mode: "identified" | "anonymous" = "anonymous";
  if (identifiedUserId === identity.userId) {
    await showChat(api, version);
    return "identified";
  }
  const wasReady = widgetReady;
  widgetReady = false;
  try {
    await callWithCallback((callback) =>
      api.login?.(
        {
          userId: identity.userId,
          hash: identity.hash,
          name: identity.name,
          email: identity.email,
          phone: identity.phone,
          role: identity.role.toLowerCase(),
        },
        callback,
      ),
    );
    assertSession(version);
    identifiedUserId = identity.userId;
    stopped = false;
    mode = "identified";
  } catch (error) {
    assertSession(version);
    if (error instanceof TawkTimeoutError) throw error;
    widgetReady = wasReady;
    // Le support reste accessible, mais aucune PII non signée n'est envoyée.
  }
  await showChat(api, version);
  api.addTags?.([mode === "identified" ? identity.role.toLowerCase() : "identity-unavailable"], () => undefined);
  return mode;
}

/** Efface l'identité tawk.to lors d'un logout ou changement de compte. */
export async function resetTawkSession(): Promise<void> {
  sessionVersion += 1;
  identifiedUserId = null;
  const api = window.Tawk_API;
  if (!api) return;

  api.hideWidget?.();
  try {
    if (api.logout) {
      await callWithCallback((callback) => api.logout?.(callback), 3_000);
    }
  } catch {
    // Best effort : le logout Alomain ne dépend jamais d'un service tiers.
  } finally {
    api.hideWidget?.();
    api.shutdown?.();
    stopped = true;
  }
}

/** Réservé aux tests : restaure l'état du singleton sans recharger le module. */
export function resetTawkRuntimeForTests(): void {
  loadPromise = null;
  widgetReady = false;
  resolveLoad = null;
  rejectLoad = null;
  openingPromise = null;
  identifiedUserId = null;
  stopped = false;
  sessionVersion = 0;
}
